import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { HandHeart, PiggyBank, CalendarDays, Handshake, ArrowRight } from "lucide-react";
import { fetchVolunteers, fetchFundraisers, fetchEvents } from "../../lib/api";
import { safeUrl } from "../../lib/safeUrl";
import { fundCategoryFor } from "../../lib/volunteerVisuals";

const Col = ({ icon: Icon, title, items, empty, all, testId }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={testId}>
    <p className="flex items-center gap-2 text-sm font-semibold text-slate-900"><Icon size={16} className="text-emerald-700" />{title}</p>
    <ul className="mt-3 space-y-2.5">
      {items === null && <li className="h-10 animate-pulse rounded-lg bg-slate-100" />}
      {items?.length === 0 && <li className="text-sm text-slate-600">{empty}</li>}
      {items?.map((o) => (
        <li key={o.id} className="text-sm">
          {o.internal ? <Link to={o.href} className="font-medium text-slate-900 hover:text-[#1E3A8A]">{o.title}</Link>
            : <a href={safeUrl(o.href)} target="_blank" rel="noopener noreferrer" className="font-medium text-slate-900 hover:text-[#1E3A8A]">{o.title}</a>}
          <span className="block text-xs text-slate-600">{o.organization} · {o.city}</span>
        </li>
      ))}
    </ul>
    <Link to={all} className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-emerald-700 hover:underline">See all <ArrowRight size={14} /></Link>
  </div>
);

export const RelatedActions = ({ cause, province }) => {
  const [v, setV] = useState(null);
  const [f, setF] = useState(null);
  const [e, setE] = useState(null);
  useEffect(() => {
    const p = province || undefined;
    fetchVolunteers({ category: cause, province: p, limit: 3 }).then((r) => setV(r.map((o) => ({ ...o, internal: true, href: `/volunteer/${o.id}` })))).catch(() => setV([]));
    fetchFundraisers({ province: p, category: fundCategoryFor(cause), limit: 3 }).then((r) => setF(r.items.map((o) => ({ ...o, href: o.donate_url || o.url })))).catch(() => setF([]));
    const ev = (r) => setE(r.items.map((o) => ({ ...o, href: o.register_url || o.signup_url })));
    fetchEvents({ province: p, cause, limit: 3 }).then((r) => (r.items.length ? ev(r) : fetchEvents({ province: p, limit: 3 }).then(ev))).catch(() => setE([]));
  }, [cause, province]);
  const q = (path, extra = "") => `${path}?${province ? `province=${province}&` : ""}${extra}`;
  return (
    <div className="rounded-3xl bg-emerald-50 p-4 sm:p-5" data-testid="how-you-can-help">
      <p className="text-base font-bold text-slate-900">How You Can Help</p>
      <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <Col icon={HandHeart} title="Volunteer" items={v} empty="No matching roles right now." all={q("/volunteer", `category=${encodeURIComponent(cause)}`)} testId="help-volunteer" />
        <Col icon={PiggyBank} title="Donate & fundraise" items={f} empty="No local fundraisers listed yet." all={q("/fundraisers")} testId="help-fundraisers" />
        <Col icon={CalendarDays} title="Attend an event" items={e} empty="No upcoming events nearby." all={q("/events")} testId="help-events" />
        <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="help-sponsor">
          <p className="flex items-center gap-2 text-sm font-semibold text-slate-900"><Handshake size={16} className="text-emerald-700" />Sponsor or mentor</p>
          <p className="mt-3 text-sm text-slate-700">Businesses and individuals can sponsor a local event or become a mentor through youth programs.</p>
          <div className="mt-3 flex flex-col gap-1.5 text-sm font-semibold text-emerald-700">
            <Link to={q("/events")} className="hover:underline">Sponsor an event →</Link>
            <Link to={q("/volunteer", "category=Youth%20Mentorship")} className="hover:underline">Become a mentor →</Link>
          </div>
        </div>
      </div>
      {[...(v || []), ...(f || []), ...(e || [])].length > 0 && (
        <p className="mt-3 text-sm text-slate-700" data-testid="help-organizations"><span className="font-semibold text-slate-900">Related organizations: </span>
          {[...new Set([...(v || []), ...(f || []), ...(e || [])].map((o) => o.organization))].slice(0, 6).join(" · ")}</p>
      )}
    </div>
  );
};
